import * as React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import { type ParentInfo, isProcedureParentInfo, isUnitParentInfo } from './Model'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { UnitImage } from '../unit/UnitImage'
import classNames from 'classnames'

interface BaseProps {
    deps: CommonDeps
    renderCachedImage: RenderCachedImage
}

interface ParentInfoProps extends BaseProps {
    parentInfo: ParentInfo[]
    onClickGuideParentInfo?: (parentInfo: ParentInfo) => void
    preview?: boolean
}

interface IconProps extends BaseProps {
    item: ParentInfo
    handleClick: <T>(event: React.UIEvent<T>) => void
}

function ParentInfoItemIcon(props: IconProps): React.ReactElement | null {
    const { deps, renderCachedImage, item, handleClick } = props
    if (isProcedureParentInfo(item)) {
        return (
            <div className="AppCore__guide__GuideParentInfo-icon" onClick={handleClick}>
                <zaptic-icon icon-name="grey-procedure-wrench-in-white-circle" />
            </div>
        )
    }

    if (isUnitParentInfo(item)) {
        return (
            <div className="AppCore__guide__GuideParentInfo-icon" onClick={handleClick}>
                <UnitImage deps={deps} renderCachedImage={renderCachedImage} unit={item.unit} />
            </div>
        )
    }

    return null
}

interface ParentInfoItemProps {
    deps: CommonDeps
    item: ParentInfo
    onClick: () => void
    renderCachedImage: RenderCachedImage
}

export function GuideParentInfoItem(props: React.PropsWithChildren<ParentInfoItemProps>) {
    const handleClick = function <T>(event: React.UIEvent<T>): void {
        event.preventDefault()
        event.stopPropagation()
        props.onClick()
    }

    return (
        <div className="AppCore__guide__GuideParentInfoItem">
            <ParentInfoItemIcon
                deps={props.deps}
                handleClick={handleClick}
                item={props.item}
                renderCachedImage={props.renderCachedImage}
            />

            <div className="AppCore__guide__GuideParentInfo-title">
                <div>
                    <zaptic-ellipsis class="AppCore__guide__GuideParentInfo-text-overflow">
                        <span onClick={handleClick}>{props.item.name}</span>
                        {props.children}
                    </zaptic-ellipsis>
                </div>
            </div>
        </div>
    )
}

function renderParentInfoItem(
    item: ParentInfo,
    index: number,
    deps: CommonDeps,
    renderCachedImage: RenderCachedImage,
    totalItems: number,
    onClickGuideParentInfo?: (parentInfo: ParentInfo) => void,
    itemsRemaining?: number
) {
    const showDelimiter = index < totalItems - 1 ? true : false
    const delimiter = ','
    const showItemsRemaining = itemsRemaining && index === totalItems - 1 ? true : false

    const handleOnClick = () => onClickGuideParentInfo && onClickGuideParentInfo(item)

    return (
        <li key={index}>
            <GuideParentInfoItem deps={deps} item={item} onClick={handleOnClick} renderCachedImage={renderCachedImage}>
                {showDelimiter ? <span>{delimiter}</span> : ''}{' '}
                {showItemsRemaining && (
                    <>
                        <span> and </span>
                        {itemsRemaining}+
                    </>
                )}
            </GuideParentInfoItem>
        </li>
    )
}

export function GuideParentInfo(props: ParentInfoProps) {
    const { deps, renderCachedImage, parentInfo, onClickGuideParentInfo, preview } = props

    let shortList: ParentInfo[] = []
    let itemsRemaining: number = 0

    const itemsToDisplay = 2
    if (parentInfo.length > itemsToDisplay) {
        shortList = parentInfo.slice(0, itemsToDisplay)
        itemsRemaining = parentInfo.length - itemsToDisplay
    }

    return (
        <div
            className={classNames('AppCore__guide__GuideParentInfo', {
                AppCore__guide__GuideParentInfo__preview: preview,
            })}
            slot="parentInfo"
        >
            {shortList.length > 0 ? (
                <ul>
                    {shortList.map((item, index) =>
                        renderParentInfoItem(
                            item,
                            index,
                            deps,
                            renderCachedImage,
                            shortList.length,
                            onClickGuideParentInfo,
                            itemsRemaining
                        )
                    )}
                </ul>
            ) : (
                <ul>
                    {parentInfo.map((item, index) =>
                        renderParentInfoItem(
                            item,
                            index,
                            deps,
                            renderCachedImage,
                            parentInfo.length,
                            onClickGuideParentInfo
                        )
                    )}
                </ul>
            )}
        </div>
    )
}
